import React, { useState, useEffect } from 'react';
import { SecondaryBase } from '../Commons/SecondaryBase';
import { useAuth } from '../../Providers/AuthProvider';
import { Confirm } from './../../Commons/Modals/Confirm';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';

export default function TriviaManager({ getOutOfTheDynamic }) {
    const { authenticatedFetch } = useAuth();
    const { showMessage } = useFloatMessage();

    const [trivias, setTrivias] = useState([]);
    const [selectedTrivia, setSelectedTrivia] = useState(null);

    // Form states
    const [triviaName, setTriviaName] = useState('');
    const [questions, setQuestions] = useState([]); // [{ tempId: string, questionText: string, options: string[], correctAnswer: string }]

    // UI States
    const [loading, setLoading] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);

    // Confirm Modal states
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalData, setConfirmModalData] = useState(null);

    const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';

    const fetchTrivias = async () => {
        setLoading(true);
        try {
            const res = await authenticatedFetch(`${API_URL}v2/trivias`);
            if (res.ok) {
                const data = await res.json();
                setTrivias(data);
            } else {
                console.error("Failed to fetch trivias", res.statusText);
            }
        } catch (error) {
            console.error("Error fetching trivias:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTrivias();
    }, []);

    const handleSelectTrivia = async (triviaItem) => {
        setLoading(true);
        try {
            const res = await authenticatedFetch(`${API_URL}v2/trivias/${triviaItem.id}`);
            if (res.ok) {
                const data = await res.json();
                setSelectedTrivia(data);
                setTriviaName(data.name);
                setQuestions(data.questions.map((q) => ({
                    tempId: q.id || Math.random().toString(),
                    id: q.id,
                    questionText: q.questionText,
                    options: q.options || [],
                    correctAnswer: q.correctAnswer
                })));
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: 'No se pudo cargar el detalle de la trivia.'
                });
            }
        } catch (error) {
            console.error("Error fetching trivia details:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setSelectedTrivia(null);
        setTriviaName('');
        setQuestions([]);
    };

    const handleAddNewTrivia = () => {
        setSelectedTrivia({ id: 'new' });
        setTriviaName('');
        setQuestions([
            {
                tempId: Math.random().toString(),
                questionText: '',
                options: ['', '', '', ''],
                correctAnswer: ''
            }
        ]);
    };

    const handleAddQuestion = () => {
        setQuestions([
            ...questions,
            {
                tempId: Math.random().toString(),
                questionText: '',
                options: ['', '', '', ''],
                correctAnswer: ''
            }
        ]);
    };

    const handleRemoveQuestion = (tempId) => {
        setQuestions(questions.filter((q) => q.tempId !== tempId));
    };

    const handleQuestionTextChange = (tempId, val) => {
        setQuestions(questions.map((q) => q.tempId === tempId ? { ...q, questionText: val } : q));
    };

    const handleOptionChange = (qTempId, optIdx, val) => {
        setQuestions(questions.map((q) => {
            if (q.tempId === qTempId) {
                const newOpts = [...q.options];
                newOpts[optIdx] = val;
                return { ...q, options: newOpts };
            }
            return q;
        }));
    };

    const handleAddOptionToQuestion = (qTempId) => {
        setQuestions(questions.map((q) => {
            if (q.tempId === qTempId) {
                return { ...q, options: [...q.options, ''] };
            }
            return q;
        }));
    };

    const handleRemoveOptionFromQuestion = (qTempId, optIdx) => {
        setQuestions(questions.map((q) => {
            if (q.tempId === qTempId) {
                return { ...q, options: q.options.filter((_, idx) => idx !== optIdx) };
            }
            return q;
        }));
    };

    const handleCorrectAnswerChange = (qTempId, val) => {
        setQuestions(questions.map((q) => q.tempId === qTempId ? { ...q, correctAnswer: val } : q));
    };

    const handleSave = async (e) => {
        e.preventDefault();

        if (!triviaName.trim()) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'El nombre del cuestionario es obligatorio.' });
            return;
        }

        if (questions.length === 0) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'El cuestionario debe tener al menos una pregunta.' });
            return;
        }

        // Validaciones de preguntas
        for (let i = 0; i < questions.length; i++) {
            const q = questions[i];
            if (!q.questionText.trim()) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: `La pregunta #${i + 1} no tiene texto.` });
                return;
            }
            const filteredOpts = q.options.filter((o) => o.trim() !== '');
            if (filteredOpts.length < 2) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: `La pregunta #${i + 1} debe tener al menos 2 opciones con texto.` });
                return;
            }
            if (!q.correctAnswer.trim()) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: `Por favor selecciona la opción correcta para la pregunta #${i + 1}.` });
                return;
            }
            if (!filteredOpts.includes(q.correctAnswer)) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: `La respuesta correcta en la pregunta #${i + 1} debe coincidir exactamente con una de las opciones.` });
                return;
            }
        }

        setActionInProgress(true);
        const payload = {
            name: triviaName,
            questions: questions.map((q, idx) => ({
                questionText: q.questionText,
                options: q.options.filter(o => o.trim() !== ''),
                correctAnswer: q.correctAnswer,
                sortOrder: idx
            }))
        };

        const isNew = selectedTrivia.id === 'new';
        const url = isNew ? `${API_URL}v2/trivias` : `${API_URL}v2/trivias/${selectedTrivia.id}`;
        const method = isNew ? 'POST' : 'PUT';

        try {
            const res = await authenticatedFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: isNew ? 'Cuestionario de trivia creado con éxito.' : 'Cuestionario de trivia actualizado con éxito.'
                });
                handleCancelEdit();
                fetchTrivias();
            } else {
                const errData = await res.json().catch(() => ({}));
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: errData.message || 'Error al guardar los cambios en la trivia.'
                });
            }
        } catch (error) {
            console.error("Error saving trivia:", error);
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ocurrió un error en la conexión.' });
        } finally {
            setActionInProgress(false);
        }
    };

    const confirmDeleteTrivia = (triviaItem) => {
        setConfirmModalData(triviaItem);
        setShowConfirmModal(true);
    };

    const handleDeleteTrivia = async () => {
        if (!confirmModalData) return;
        setActionInProgress(true);
        try {
            const res = await authenticatedFetch(`${API_URL}v2/trivias/${confirmModalData.id}`, {
                method: 'DELETE'
            });

            if (res.ok) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Cuestionario de trivia eliminado.' });
                fetchTrivias();
                if (selectedTrivia && selectedTrivia.id === confirmModalData.id) {
                    handleCancelEdit();
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'No se pudo eliminar el cuestionario.' });
            }
        } catch (error) {
            console.error("Error deleting trivia:", error);
        } finally {
            setActionInProgress(false);
            setShowConfirmModal(false);
            setConfirmModalData(null);
        }
    };

    return (
        <SecondaryBase
            title="Administrador de Trivias"
            getOutOfTheDynamic={getOutOfTheDynamic}
        >
            <div className="container-fluid py-3 text-white">
                <div className="row">
                    {/* Lista de Trivias */}
                    <div className="col-md-5 mb-4">
                        <div className="card bg-dark border-secondary h-100">
                            <div className="card-header border-secondary d-flex justify-content-between align-items-center">
                                <h5 className="mb-0 text-info fw-bold">Cuestionarios Disponibles</h5>
                                <button
                                    className="btn btn-outline-info btn-sm"
                                    onClick={handleAddNewTrivia}
                                    disabled={loading || actionInProgress}
                                >
                                    <i className="bi bi-plus-circle me-1"></i>Nuevo
                                </button>
                            </div>
                            <div className="card-body p-0" style={{ maxHeight: '550px', overflowY: 'auto' }}>
                                {loading && trivias.length === 0 ? (
                                    <div className="text-center py-4">Cargando cuestionarios...</div>
                                ) : trivias.length === 0 ? (
                                    <div className="text-center py-4 text-muted">No hay cuestionarios creados aún.</div>
                                ) : (
                                    <div className="list-group list-group-flush bg-transparent">
                                        {trivias.map((t) => (
                                            <div
                                                key={t.id}
                                                className={`list-group-item list-group-item-action bg-transparent text-white border-secondary d-flex justify-content-between align-items-center ${selectedTrivia?.id === t.id ? 'active bg-secondary text-info' : ''}`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleSelectTrivia(t)}
                                            >
                                                <div>
                                                    <span className="fw-bold">{t.name}</span>
                                                    <div className="small text-muted">{t.questionsCount} preguntas | {new Date(t.createdAt).toLocaleDateString()}</div>
                                                </div>
                                                <button
                                                    className="btn btn-outline-danger btn-sm border-0"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        confirmDeleteTrivia(t);
                                                    }}
                                                    disabled={actionInProgress}
                                                >
                                                    <i className="bi bi-trash-fill"></i>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Editor Form */}
                    <div className="col-md-7 mb-4">
                        {selectedTrivia ? (
                            <div className="card bg-dark border-secondary">
                                <div className="card-header border-secondary">
                                    <h5 className="mb-0 text-info fw-bold">
                                        {selectedTrivia.id === 'new' ? 'Crear Nuevo Cuestionario' : 'Editar Cuestionario'}
                                    </h5>
                                </div>
                                <form onSubmit={handleSave}>
                                    <div className="card-body">
                                        {/* Nombre */}
                                        <div className="mb-4">
                                            <label className="form-label text-muted fw-bold">Nombre del Cuestionario</label>
                                            <input
                                                type="text"
                                                className="form-control bg-transparent text-white border-secondary"
                                                placeholder="Ej: Trivia de Medio Tiempo - Clásico"
                                                value={triviaName}
                                                onChange={(e) => setTriviaName(e.target.value)}
                                                required
                                                disabled={actionInProgress}
                                            />
                                        </div>

                                        <hr className="border-secondary mb-4" />

                                        {/* Preguntas */}
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h6 className="mb-0 text-info fw-bold">Preguntas de la Trivia</h6>
                                            <button
                                                type="button"
                                                className="btn btn-outline-success btn-sm"
                                                onClick={handleAddQuestion}
                                                disabled={actionInProgress}
                                            >
                                                <i className="bi bi-plus-lg me-1"></i>Agregar Pregunta
                                            </button>
                                        </div>

                                        <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                                            {questions.map((q, idx) => (
                                                <div key={q.tempId} className="card bg-transparent border-secondary mb-4">
                                                    <div className="card-header border-secondary bg-secondary bg-opacity-25 d-flex justify-content-between align-items-center py-2">
                                                        <span className="fw-bold text-info">Pregunta #{idx + 1}</span>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger btn-sm py-0 border-0"
                                                            onClick={() => handleRemoveQuestion(q.tempId)}
                                                            disabled={actionInProgress}
                                                        >
                                                            <i className="bi bi-x-circle-fill fs-5"></i>
                                                        </button>
                                                    </div>
                                                    <div className="card-body">
                                                        {/* Texto de la pregunta */}
                                                        <div className="mb-3">
                                                            <label className="form-label text-muted small fw-bold">Texto de la Pregunta</label>
                                                            <input
                                                                type="text"
                                                                className="form-control bg-transparent text-white border-secondary"
                                                                placeholder="Ej: ¿En qué año se fundó el club?"
                                                                value={q.questionText}
                                                                onChange={(e) => handleQuestionTextChange(q.tempId, e.target.value)}
                                                                required
                                                                disabled={actionInProgress}
                                                            />
                                                        </div>

                                                        {/* Opciones */}
                                                        <div className="mb-3">
                                                            <label className="form-label text-muted small fw-bold d-flex justify-content-between">
                                                                <span>Opciones de Respuesta</span>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link text-success p-0 text-decoration-none small"
                                                                    onClick={() => handleAddOptionToQuestion(q.tempId)}
                                                                    disabled={actionInProgress || q.options.length >= 6}
                                                                >
                                                                    + Agregar Opción
                                                                </button>
                                                            </label>
                                                            {q.options.map((opt, optIdx) => (
                                                                <div key={optIdx} className="input-group mb-2">
                                                                    <span className="input-group-text bg-secondary border-secondary text-white">{String.fromCharCode(65 + optIdx)}</span>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control bg-transparent text-white border-secondary"
                                                                        placeholder={`Opción ${String.fromCharCode(65 + optIdx)}`}
                                                                        value={opt}
                                                                        onChange={(e) => handleOptionChange(q.tempId, optIdx, e.target.value)}
                                                                        required
                                                                        disabled={actionInProgress}
                                                                    />
                                                                    {q.options.length > 2 && (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-danger"
                                                                            onClick={() => handleRemoveOptionFromQuestion(q.tempId, optIdx)}
                                                                            disabled={actionInProgress}
                                                                        >
                                                                            <i className="bi bi-dash"></i>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>

                                                        {/* Respuesta Correcta (Dropdown) */}
                                                        <div>
                                                            <label className="form-label text-muted small fw-bold">Respuesta Correcta</label>
                                                            <select
                                                                className="form-select bg-dark text-white border-secondary"
                                                                value={q.correctAnswer}
                                                                onChange={(e) => handleCorrectAnswerChange(q.tempId, e.target.value)}
                                                                required
                                                                disabled={actionInProgress}
                                                            >
                                                                <option value="">-- Selecciona la opción correcta --</option>
                                                                {q.options.filter(o => o.trim() !== '').map((opt, optIdx) => (
                                                                    <option key={optIdx} value={opt}>
                                                                        {String.fromCharCode(65 + optIdx)}) {opt}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="card-footer border-secondary d-flex justify-content-end gap-2 bg-transparent">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={handleCancelEdit}
                                            disabled={actionInProgress}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-info px-4"
                                            disabled={actionInProgress}
                                        >
                                            {actionInProgress ? 'Guardando...' : 'Guardar Cuestionario'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        ) : (
                            <div className="h-100 d-flex flex-column align-items-center justify-content-center border border-secondary border-dashed rounded p-5 text-muted bg-dark bg-opacity-25" style={{ minHeight: '350px' }}>
                                <i className="bi bi-file-earmark-ruled fs-1 text-secondary mb-3"></i>
                                <h5>Selecciona un cuestionario para editar o crea uno nuevo</h5>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Confirm Delete Modal */}
            {showConfirmModal && (
                <Confirm
                    message={`¿Estás seguro de que deseas eliminar permanentemente el cuestionario "${confirmModalData?.name}"?`}
                    onAccept={handleDeleteTrivia}
                    onCancel={() => setShowConfirmModal(false)}
                />
            )}
        </SecondaryBase>
    );
}
